<template>
  <div class="home">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="page-header">
            <button
              v-if="$route.meta.allowExport"
              @click="exportCsv"
              type="button"
              class="btn btn-info float-right mx-1 my-1">
              <i class="fa fa-download" />
              Export {{ databaseItems.searchForm.plural }}
            </button>
            <button
              @click="newObject($route.meta.newPermalink)"
              v-if="$route.meta.newPermalink"
              type="button"
              class="btn btn-primary float-right mx-1 my-1">
              <i class="fa fa-plus" />
              New {{ databaseItems.searchForm.singular }}
            </button>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 mt-5">
          <div v-if="$route.meta.showSearch" @click="toggleSearch()" v-show="searchHidden">
            <p class="form-control text-muted fake-input interactive-cursor">
              Search...
              <span class="fa fa-search float-right"></span>
            </p>
          </div>
          <div v-show="!searchHidden">
            <vue-form
              ref="form"
              :sections="databaseSearchForm.fields"
              :options="{
                submit: 'Search',
                submitting: 'Searching...',
                permalink: $route.meta.vueData,
                method: 'search'
              }">
              <span slot="title">
                <i class="fa fa-search fa-fw"></i> Search {{ databaseItems.searchForm.plural }}
                <span class="fa fa-times float-right fa-lg text-danger interactive-cursor" @click="toggleSearch()"></span>
              </span>
              <div class="form-group" slot="actions">
                <div class="col-sm-8 col-sm-offset-4">
                  <button class="btn btn-success" type="button" @click="refresh()">Search</button>
                </div>
              </div>
            </vue-form>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <paging-input ref="paging"
            @change="changePage"
            :options="{
              current: (databaseItems.offset + parseInt(databaseItems.limit)) / parseInt(databaseItems.limit),
              totalResults: databaseItems.count,
              limit: parseInt(databaseItems.limit)
            }"
            v-if="databaseItems.count">
          </paging-input>
          <responsive-table
            @orderColumn="sort($event)"
            :hideTitle="true"
            :table="{ columns: databaseItems.columns }"
            :default-sort-column="sortColumn"
            :default-sort="sortDirection">
            <template slot="rows">
              <template v-for="(row, i) in databaseItems.rows">
                <component
                  @edit="editObject($event)"
                  @deleteObject="deleteObject($event)"
                  :is="parseType($route.meta.vueData) + '-row'"
                  :object="row"
                  :index="i"
                  :key="i">
                </component>
              </template>
            </template>
          </responsive-table>
          <paging-input ref="paging"
            @change="changePage"
            :options="{
              current: (databaseItems.offset + parseInt(databaseItems.limit)) / parseInt(databaseItems.limit),
              totalResults: databaseItems.count,
              limit: parseInt(databaseItems.limit)
            }"
            v-if="databaseItems.count">
          </paging-input>
        </div>
      </div>
    </div>
    <form action="/api/admin/export" method="post" ref="exportForm">
      <input type="hidden" name="exportObjects" v-model="$route.meta.vueData"/>
    </form>
  </div>
</template>

<script>
  import {
    mapGetters
  } from 'vuex'
  import {
    DELETE_DATABASE_OBJECT,
    EDIT_DATABASE_OBJECT,
    LOAD_DATA,
    NEW_DATABASE_OBJECT
  } from '@/vuex/types'
  import VueForm from '@/components/Form.vue'
  import ResponsiveTable from '@/components/ui/ResponsiveTable.vue'
  import PagingInput from '@/components/inputs/PagingInput.vue'
  // ROWS ------- //
  import AppsRow from '@/components/rows/App.vue'
  import ContractorRow from '@/components/rows/Contractor.vue'
  import EsmItemRow from '@/components/rows/EsmItem.vue'
  import EsmTemplateRow from '@/components/rows/EsmTemplate.vue'
  import OcManagerRow from '@/components/rows/OcManager.vue'
  import OhsFindingRow from '@/components/rows/OhsFinding.vue'
  import OhsLocationRow from '@/components/rows/OhsLocation.vue'
  import PropertiesRow from '@/components/rows/Property.vue'
  import EPMARow from '@/components/rows/EPMA.vue'
  import ReportTypeRow from '@/components/rows/ReportType.vue'
  import StaffRow from '@/components/rows/Staff.vue'
  import StaffTaxInvoiceReportRow from '@/components/rows/StaffTaxInvoiceReport.vue'
  import CompanyRow from '@/components/rows/Company.vue'
  import VideoRow from '@/components/rows/Video.vue'
  import PropertyCostingRow from '@/components/rows/PropertyCosting.vue'
  import InvoiceCodeRow from '@/components/rows/InvoiceCode.vue'

  export default {
    name: 'DatabaseTableDisplay',
    components: {
      AppsRow,
      ContractorRow,
      EsmItemRow,
      EsmTemplateRow,
      InvoiceCodeRow,
      OcManagerRow,
      OhsFindingRow,
      OhsLocationRow,
      PagingInput,
      PropertiesRow,
      EPMARow,
      ReportTypeRow,
      PropertyCostingRow,
      ResponsiveTable,
      StaffRow,
      CompanyRow,
      VueForm,
      VideoRow,
      StaffTaxInvoiceReportRow
    },
    data () {
      return {
        sortColumn: false,
        sortDirection: false,
        searchForm: [],
        searchOffset: 0,
        searchLimit: 25,
        searchHidden: true,
        newSearch: true,
        noResults: 'Your search returned 0 results',
        pagingOptions: {
          totalResults: 10,
          limit: 10,
          current: 1
        },
        newPermalink: false
      }
    },
    computed: {
      ...mapGetters([
        'databaseItems',
        'databaseSearchForm'
      ])
    },
    mounted () {
      this.createNewSearch()
    },
    watch: {
      $route () {
        this.createNewSearch()
      }
    },
    methods: {
      async exportCsv () {
        await this.$nextTick()
        this.$refs.exportForm.submit()
      },
      sort (column) {
        this.sortColumn = column.name
        this.sortDirection = column.sortDirection
        this.search()
      },
      async createNewSearch () {
        this.searchOffset = 0
        let loadData = {
          dataName: this.$route.meta.vueData,
          searchForm: false,
          offset: this.searchOffset,
          limit: this.databaseItems.limit,
          newSearch: true,
          sortColumn: this.sortColumn,
          sortDirection: this.sortDirection
        }
        // Added ocManagerUuid to make Manager name as a hyperlink to edit its details under DatabaseSearch.
        if (this.$route.params.ocManagerUuid) {
          loadData.ocManagerUuid = this.$route.params.ocManagerUuid
        }
        await this.$store.dispatch(LOAD_DATA, loadData)
      },
      async refresh () {
        this.searchOffset = 0
        await this.search()
      },
      async search () {
        await this.$refs.form.validateForm()
        if (this.$refs.form.validForm) {
          let searchForm = this.databaseSearchForm
          let limit = searchForm.fields.splice(-1, 1)
          await this.$store.dispatch(LOAD_DATA, {
            dataName: this.$route.meta.vueData,
            searchForm,
            offset: this.searchOffset,
            limit: limit[0].value,
            newSearch: this.newSearch,
            sortColumn: this.sortColumn,
            sortDirection: this.sortDirection
          })
        }
      },
      async toggleSearch () {
        this.searchHidden = !this.searchHidden
        if (!this.searchHidden) {
          await this.$nextTick()
          this.$refs.form.$el.querySelectorAll('input[type=text]')[0].focus()
        }
      },
      async changePage (page) {
        this.searchOffset = (page - 1) * this.databaseItems.limit
        await this.search()
      },
      parseType (type) {
        switch (type) {
          case 'invoiceAccountCodes':
            return 'invoice-code'
          case 'ocManagers':
            return 'oc-manager'
          case 'companies':
            return 'company'
          case 'epma':
            return 'epma'
          case 'ohsHazardLocations':
            return 'ohs-location'
          case 'ohsHazardFindings':
            return 'ohs-finding'
          case 'esmItems':
            return 'esm-item'
          case 'esmTemplates':
            return 'esm-template'
          case 'contractors':
            return 'contractor'
          case 'reportTypes':
            return 'report-type'
          case 'propertyCosting':
            return 'property-costing'
          case 'videos':
            return 'video'
          case 'staffTaxInvoiceReport':
            return 'staff-tax-invoice-report'
          default:
            return type
        }
      },
      async editObject (payload) {
        await this.$store.dispatch(EDIT_DATABASE_OBJECT, payload)
      },
      async deleteObject (payload) {
        await this.$store.dispatch(DELETE_DATABASE_OBJECT, payload)
      },
      async newObject (permalink) {
        await this.$store.dispatch(NEW_DATABASE_OBJECT, permalink)
      }
    }
  }

</script>



// WEBPACK FOOTER //
// src/components/admin/DatabaseTableDisplay.vue